/**
 * Here is all of our MUI typography theme
 * Changing values in here will affect the entire application
 */
import React from "react";

import { Theme } from "@mui/material";

const fontFamilyWorkSans = ['"Work Sans"', "sans-serif"].join(",");
const fontFamilyInter = ['"Inter"', "sans-serif"].join(",");
const fontFamilyOpenSans = ['"Open Sans"', "sans-serif"].join(",");

// Need to specify fontFamily when creating the theme
// so that mui will set the default for all typography
const defaults = {
  typography: {
    fontFamily: fontFamilyWorkSans,
    fontFamilySecondary: fontFamilyInter,
    fontFamilyOpenSans: fontFamilyOpenSans,
  },
};

declare module "@mui/material/styles/createTypography" {
  interface FontStyle {
    fontFamilySecondary: React.CSSProperties["fontFamily"];
    fontFamilyOpenSans: React.CSSProperties["fontFamily"];
  }
}

// https://next.material-ui.com/customization/typography/#adding-amp-disabling-variants
// Currently, all variants/variantoptions/overrides from all apps (analytics/embedded/common) need to live in these interfaces
declare module "@mui/material/styles" {
  interface FontStyle {
    fontFamilySecondary: React.CSSProperties["fontFamily"];
    fontFamilyOpenSans: React.CSSProperties["fontFamily"];
  }

  interface TypographyVariants {
    bodyPrimaryReg: React.CSSProperties;
    bodyPrimaryMed: React.CSSProperties;
    bodyPrimaryLink: React.CSSProperties;
    bodySecondaryReg: React.CSSProperties;
    bodySecondaryMed: React.CSSProperties;
    bodySecondaryLink: React.CSSProperties;
    labelReg: React.CSSProperties;
    labelMed: React.CSSProperties;
    labelLink: React.CSSProperties;
    statPrimary: React.CSSProperties;
    statSecondary: React.CSSProperties;
    statTertiary: React.CSSProperties;
    detailReg: React.CSSProperties;
    detailMed: React.CSSProperties;
    disclaimer: React.CSSProperties;
    subtext: React.CSSProperties;
    subtext2: React.CSSProperties;
    cardBody1: React.CSSProperties;
    label1: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bodyPrimaryReg?: React.CSSProperties;
    bodyPrimaryMed?: React.CSSProperties;
    bodyPrimaryLink?: React.CSSProperties;
    bodySecondaryReg?: React.CSSProperties;
    bodySecondaryMed?: React.CSSProperties;
    bodySecondaryLink?: React.CSSProperties;
    labelReg?: React.CSSProperties;
    labelMed?: React.CSSProperties;
    labelLink?: React.CSSProperties;
    statPrimary?: React.CSSProperties;
    statSecondary?: React.CSSProperties;
    statTertiary?: React.CSSProperties;
    detailReg?: React.CSSProperties;
    detailMed?: React.CSSProperties;
    disclaimer?: React.CSSProperties;
    subtext?: React.CSSProperties;
    subtext2?: React.CSSProperties;
    cardBody1?: React.CSSProperties;
    label1?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface FontStyle {
    fontFamilySecondary: React.CSSProperties["fontFamily"];
    fontFamilyOpenSans: React.CSSProperties["fontFamily"];
  }

  interface TypographyPropsVariantOverrides {
    bodyPrimaryReg: true;
    bodyPrimaryMed: true;
    bodyPrimaryLink: true;
    bodySecondaryReg: true;
    bodySecondaryMed: true;
    bodySecondaryLink: true;
    labelReg: true;
    labelMed: true;
    labelLink: true;
    statPrimary: true;
    statSecondary: true;
    statTertiary: true;
    detailReg: true;
    detailMed: true;
    disclaimer: true;
    subtext: true;
    subtext2: true;
    cardBody1: true;
    label1: true;
  }
}
const bodyPrimary = {
  fontFamily: fontFamilyWorkSans,
  fontSize: "1rem",
  lineHeight: 1.25,
};
const bodySecondary = {
  fontFamily: fontFamilyWorkSans,
  fontSize: "0.875rem",
  lineHeight: 1.25,
};
const label = {
  fontFamily: fontFamilyWorkSans,
  fontSize: "0.75rem",
  lineHeight: 1.33,
  letterSpacing: "-0.01em",
};

const detail = {
  fontFamily: fontFamilyInter,
  fontSize: "0.875rem",
};

const typography = (_theme: Theme) => ({
  typography: {
    h1: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.167,
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.3125rem",
      lineHeight: 1.19,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.167,
    },
    bodyPrimaryReg: {
      ...bodyPrimary,
      fontWeight: 400,
    },
    bodyPrimaryMed: {
      ...bodyPrimary,
      fontWeight: 500,
    },
    bodyPrimaryLink: {
      ...bodyPrimary,
      fontWeight: 500,
      textDecoration: "underline",
    },
    bodySecondaryReg: {
      ...bodySecondary,
      fontWeight: 400,
    },
    bodySecondaryMed: {
      ...bodySecondary,
      fontWeight: 500,
    },
    bodySecondaryLink: {
      ...bodySecondary,
      fontWeight: 500,
      textDecoration: "underline",
    },
    labelReg: {
      ...label,
      fontWeight: 400,
    },
    labelMed: {
      ...label,
      fontWeight: 500,
    },
    formLabelMed: {
      fontFamily: fontFamilyInter,
      fontWeight: 500,
      fontSize: ".81rem",
    },
    labelLink: {
      ...label,
      fontWeight: 500,
      textDecoration: "underline",
    },
    detailReg: {
      ...detail,
      fontWeight: 400,
    },
    detailMed: {
      ...detail,
      fontWeight: 500,
    },
    disclaimer: {
      fontSize: "0.6875rem",
      fontWeight: 400,
      lineHeight: 1.27,
    },
    labelUpper: {
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.04rem",
      fontWeight: 400,
      lineHeight: 0.875,
    },
    button: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.15,
      textTransform: "none",
    },
    statPrimary: {
      fontWeight: 600,
      fontSize: "2.25rem",
      lineHeight: 1.15,
    },
    statSecondary: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.15,
    },
    statTertiary: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.15,
    },
  },
});

export { defaults };
export default typography;
